import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, MapPin, Send, Linkedin, Twitter, Instagram, CheckCircle } from "lucide-react";

const services = [
  "Content Creation",
  "SEO Consultancy",
  "GEO / AEO Consultancy",
  "LinkedIn Marketing",
  "Full Growth Package",
];

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "", email: "", company: "", service: "", budget: "", message: "",
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 1200);
  };

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      data-testid="contact-page"
      style={{ backgroundColor: "#0a0a0f", paddingTop: "5rem" }}
    >
      {/* Header */}
      <section className="section-padding pb-10" style={{ backgroundColor: "#0a0a0f" }}>
        <div className="container-custom text-center">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-xs font-mono uppercase tracking-widest mb-3"
            style={{ color: "#4F8EF7" }}
          >
            Get in Touch
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-white mb-5"
            data-testid="contact-heading"
          >
            Let's <span className="gradient-text">Engineer</span> Your Growth
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-base max-w-xl mx-auto"
            style={{ color: "#a1a1aa" }}
          >
            Ready to accelerate? Fill in the form below and our team will reach out within one business day.
          </motion.p>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-24" style={{ backgroundColor: "#0a0a0f" }}>
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
            {/* Left: contact info */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <h2 className="font-heading font-bold text-white text-xl mb-6">Contact Information</h2>

                <div className="space-y-5 mb-10">
                  {[
                    { icon: Mail, label: "Email", value: "hello@engeniusmarketing.com", href: "mailto:hello@engeniusmarketing.com" },
                    { icon: MapPin, label: "Office", value: "Bengaluru, India", href: null },
                  ].map(({ icon: Icon, label, value, href }) => (
                    <div key={label} className="flex items-start gap-4" data-testid={`contact-info-${label.toLowerCase()}`}>
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ background: "rgba(79,142,247,0.1)", border: "1px solid rgba(79,142,247,0.2)" }}
                      >
                        <Icon size={17} style={{ color: "#4F8EF7" }} strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="text-xs font-mono uppercase tracking-widest mb-1" style={{ color: "#52525b" }}>{label}</p>
                        {href ? (
                          <a href={href} className="text-sm transition-colors" style={{ color: "#a1a1aa" }}
                            onMouseEnter={(e) => (e.target.style.color = "#4F8EF7")}
                            onMouseLeave={(e) => (e.target.style.color = "#a1a1aa")}
                          >{value}</a>
                        ) : (
                          <p className="text-sm" style={{ color: "#a1a1aa" }}>{value}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* What to expect */}
                <div
                  className="p-6 rounded-2xl mb-8"
                  style={{ background: "rgba(79,142,247,0.05)", border: "1px solid rgba(79,142,247,0.15)" }}
                >
                  <h3 className="font-heading font-semibold text-white text-sm mb-4">What happens next?</h3>
                  <ul className="space-y-3">
                    {[
                      "We review your submission within 24 hours",
                      "A strategist reaches out to schedule a call",
                      "You receive a tailored growth proposal",
                    ].map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <div
                          className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{ background: "rgba(79,142,247,0.15)", border: "1px solid rgba(79,142,247,0.3)" }}
                        >
                          <span className="text-xs font-bold" style={{ color: "#4F8EF7" }}>{i + 1}</span>
                        </div>
                        <span className="text-sm" style={{ color: "#71717a" }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Socials */}
                <div className="flex items-center gap-3">
                  {[
                    { icon: Linkedin, href: "#", label: "LinkedIn" },
                    { icon: Twitter, href: "#", label: "Twitter" },
                    { icon: Instagram, href: "#", label: "Instagram" },
                  ].map(({ icon: Icon, href, label }) => (
                    <motion.a
                      key={label}
                      href={href}
                      data-testid={`contact-social-${label.toLowerCase()}`}
                      aria-label={label}
                      whileHover={{ scale: 1.12, y: -2 }}
                      className="w-10 h-10 rounded-xl flex items-center justify-center transition-all"
                      style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: "#71717a" }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = "rgba(79,142,247,0.4)"; e.currentTarget.style.color = "#4F8EF7"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; e.currentTarget.style.color = "#71717a"; }}
                    >
                      <Icon size={16} />
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Right: form */}
            <div className="lg:col-span-3">
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.25 }}
                className="rounded-2xl p-8 md:p-10"
                style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)" }}
              >
                {submitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                    className="text-center py-10"
                    data-testid="contact-success"
                  >
                    <div
                      className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-5"
                      style={{ background: "rgba(79,142,247,0.1)", border: "1px solid rgba(79,142,247,0.3)" }}
                    >
                      <CheckCircle size={32} style={{ color: "#4F8EF7" }} />
                    </div>
                    <h3 className="font-heading font-bold text-white text-2xl mb-3">Message Sent!</h3>
                    <p className="text-sm" style={{ color: "#a1a1aa" }}>
                      We've received your message and will reach out within one business day.
                    </p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      {/* Name */}
                      <div>
                        <label className="block text-xs font-medium mb-2 uppercase tracking-widest" style={{ color: "#52525b" }}>
                          Full Name *
                        </label>
                        <input
                          type="text"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Jane Smith"
                          data-testid="contact-input-name"
                          className="w-full px-4 py-3 rounded-xl text-sm text-white outline-none transition-all duration-200"
                          style={{
                            background: "rgba(255,255,255,0.04)",
                            border: "1px solid rgba(255,255,255,0.08)",
                            caretColor: "#4F8EF7",
                          }}
                          onFocus={(e) => { e.target.style.borderColor = "rgba(79,142,247,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(79,142,247,0.08)"; e.target.style.placeholder = ""; }}
                          onBlur={(e) => { e.target.style.borderColor = "rgba(255,255,255,0.08)"; e.target.style.boxShadow = "none"; }}
                        />
                        <style>{`input::placeholder, textarea::placeholder { color: #52525b; }`}</style>
                      </div>

                      {/* Email */}
                      <div>
                        <label className="block text-xs font-medium mb-2 uppercase tracking-widest" style={{ color: "#52525b" }}>
                          Work Email *
                        </label>
                        <input
                          type="email"
                          name="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="jane@company.com"
                          data-testid="contact-input-email"
                          className="w-full px-4 py-3 rounded-xl text-sm text-white outline-none transition-all duration-200"
                          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", caretColor: "#4F8EF7" }}
                          onFocus={(e) => { e.target.style.borderColor = "rgba(79,142,247,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(79,142,247,0.08)"; }}
                          onBlur={(e) => { e.target.style.borderColor = "rgba(255,255,255,0.08)"; e.target.style.boxShadow = "none"; }}
                        />
                      </div>
                    </div>

                    {/* Company */}
                    <div>
                      <label className="block text-xs font-medium mb-2 uppercase tracking-widest" style={{ color: "#52525b" }}>
                        Company Name
                      </label>
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        placeholder="Acme Corp"
                        data-testid="contact-input-company"
                        className="w-full px-4 py-3 rounded-xl text-sm text-white outline-none transition-all duration-200"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", caretColor: "#4F8EF7" }}
                        onFocus={(e) => { e.target.style.borderColor = "rgba(79,142,247,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(79,142,247,0.08)"; }}
                        onBlur={(e) => { e.target.style.borderColor = "rgba(255,255,255,0.08)"; e.target.style.boxShadow = "none"; }}
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      {/* Service */}
                      <div>
                        <label className="block text-xs font-medium mb-2 uppercase tracking-widest" style={{ color: "#52525b" }}>
                          Service Interested In
                        </label>
                        <select
                          name="service"
                          value={formData.service}
                          onChange={handleChange}
                          data-testid="contact-select-service"
                          className="w-full px-4 py-3 rounded-xl text-sm text-white outline-none transition-all duration-200 appearance-none"
                          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: formData.service ? "white" : "#52525b" }}
                          onFocus={(e) => { e.target.style.borderColor = "rgba(79,142,247,0.5)"; }}
                          onBlur={(e) => { e.target.style.borderColor = "rgba(255,255,255,0.08)"; }}
                        >
                          <option value="" style={{ background: "#12121a", color: "#52525b" }}>Select a service</option>
                          {services.map((s) => (
                            <option key={s} value={s} style={{ background: "#12121a", color: "white" }}>{s}</option>
                          ))}
                        </select>
                      </div>

                      {/* Budget */}
                      <div>
                        <label className="block text-xs font-medium mb-2 uppercase tracking-widest" style={{ color: "#52525b" }}>
                          Monthly Budget
                        </label>
                        <select
                          name="budget"
                          value={formData.budget}
                          onChange={handleChange}
                          data-testid="contact-select-budget"
                          className="w-full px-4 py-3 rounded-xl text-sm outline-none transition-all duration-200 appearance-none"
                          style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", color: formData.budget ? "white" : "#52525b" }}
                          onFocus={(e) => { e.target.style.borderColor = "rgba(79,142,247,0.5)"; }}
                          onBlur={(e) => { e.target.style.borderColor = "rgba(255,255,255,0.08)"; }}
                        >
                          <option value="" style={{ background: "#12121a", color: "#52525b" }}>Select range</option>
                          {["Under £1,000", "£1,000–£3,000", "£3,000–£7,500", "£7,500–£15,000", "£15,000+"].map((b) => (
                            <option key={b} value={b} style={{ background: "#12121a", color: "white" }}>{b}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Message */}
                    <div>
                      <label className="block text-xs font-medium mb-2 uppercase tracking-widest" style={{ color: "#52525b" }}>
                        Tell Us About Your Goals *
                      </label>
                      <textarea
                        name="message"
                        required
                        value={formData.message}
                        onChange={handleChange}
                        rows={4}
                        placeholder="Describe your current marketing challenges and what you'd like to achieve..."
                        data-testid="contact-textarea-message"
                        className="w-full px-4 py-3 rounded-xl text-sm text-white outline-none transition-all duration-200 resize-none"
                        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", caretColor: "#4F8EF7" }}
                        onFocus={(e) => { e.target.style.borderColor = "rgba(79,142,247,0.5)"; e.target.style.boxShadow = "0 0 0 3px rgba(79,142,247,0.08)"; }}
                        onBlur={(e) => { e.target.style.borderColor = "rgba(255,255,255,0.08)"; e.target.style.boxShadow = "none"; }}
                      />
                    </div>

                    {/* Submit */}
                    <motion.button
                      type="submit"
                      disabled={loading}
                      data-testid="contact-submit-button"
                      whileHover={{ scale: loading ? 1 : 1.02 }}
                      whileTap={{ scale: loading ? 1 : 0.98 }}
                      className="w-full flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-semibold text-white text-sm transition-all duration-300"
                      style={{
                        background: loading ? "rgba(79,142,247,0.4)" : "linear-gradient(135deg, #4F8EF7, #8B5CF6)",
                        boxShadow: loading ? "none" : "0 0 25px rgba(79,142,247,0.35)",
                        cursor: loading ? "not-allowed" : "pointer",
                      }}
                    >
                      {loading ? (
                        <>
                          <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message <Send size={16} />
                        </>
                      )}
                    </motion.button>

                    <p className="text-xs text-center" style={{ color: "#52525b" }}>
                      We respect your privacy. Your data is never sold or shared.
                    </p>
                  </form>
                )}
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </motion.main>
  );
};

export default Contact;
